import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Mail, Lock } from 'lucide-react';
import Input from '../../components/Input';
import { useRegisterMutation } from '../../store/services/api';
import { setCredentials } from '../../store/slices/authSlice';
import { useDispatch } from 'react-redux';
import type { RegisterData } from '../../api/auth';
import { toast } from 'react-toastify';
// Şimdilik devre dışı bırakıyoruz
// import TurnstileWidget from '../../components/TurnstileWidget';

interface RegisterErrors {
  fullName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  terms?: string;
  general?: string;
}

const Register: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [register, { isLoading }] = useRegisterMutation();
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    termsAccepted: false
  });
  const [errors, setErrors] = useState<RegisterErrors>({});
  // Şimdilik kullanmıyoruz
  // const [turnstileToken, setTurnstileToken] = useState('');

  const validateForm = (): boolean => {
    const newErrors: RegisterErrors = {};
    let isValid = true;
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
      isValid = false;
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
      isValid = false;
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
      isValid = false;
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
      isValid = false;
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
      isValid = false;
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }

    if (!formData.termsAccepted) {
      newErrors.terms = 'You must accept the terms and conditions';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  /* Turnstile verification disabled
  // Turnstile doğrulamasını aktif et
  const handleTurnstileVerify = (token: string) => {
    console.log("Turnstile token received:", token ? "valid token" : "empty token");
    setTurnstileToken(token);
    
    // Token geldiğinde hataları temizle
    if (token) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.turnstile;
        delete newErrors.general;
        return newErrors;
      });
    }
  };
  */

  // Localhost kontrolü (render için)
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setErrors({});
    
    const registerData: RegisterData = {
      full_name: formData.fullName,
      email: formData.email,
      password: formData.password,
      password_confirmation: formData.confirmPassword,
      terms_accepted: formData.termsAccepted
    };

    try {
      const response = await register(registerData).unwrap();
      
      if (response && response.token) {
        // First set the credentials in Redux store
        dispatch(setCredentials(response));
        
        // Show success message
        toast.success('Registration successful!');
        
        // Small delay to ensure state is updated
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 100);
      } else {
        setErrors(prev => ({ ...prev, general: 'Registration failed: No token received' }));
      }
    } catch (err: any) {
      if (err?.data?.message?.toLowerCase().includes('email')) {
        setErrors(prev => ({ ...prev, email: err.data.message }));
      } else if (err?.data?.message?.toLowerCase().includes('password')) {
        setErrors(prev => ({ ...prev, password: err.data.message }));
      } else {
        setErrors(prev => ({ ...prev, general: err?.data?.message || 'Registration failed. Please try again.' }));
      }
    }
  };

  return (
    <div className="h-screen flex items-center justify-center bg-white">
      <div className="w-full max-w-md px-8">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Create an account</h2>
          <p className="mt-2 text-sm text-gray-600">
            Join us to start sharing your memories
          </p>
        </div>

        {errors.general && (
          <div className="mb-4 bg-red-50 p-3 rounded-md text-red-600 text-sm">
            {errors.general}
          </div>
        )}

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="relative">
            <Input
              label="Full Name"
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder="Enter your full name"
              error={errors.fullName}
              className="pl-10"
              disabled={isLoading}
            />
            <User className="w-4 h-4 text-gray-400 absolute left-3 top-[34px]" />
          </div>

          <div className="relative">
            <Input
              label="Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              error={errors.email}
              className="pl-10"
              disabled={isLoading}
            />
            <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-[34px]" />
          </div>

          <div className="relative">
            <Input
              label="Password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Create a password"
              error={errors.password}
              className="pl-10"
              disabled={isLoading}
            />
            <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-[34px]" />
          </div>

          <div className="relative">
            <Input
              label="Confirm Password"
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm your password"
              error={errors.confirmPassword}
              className="pl-10"
              disabled={isLoading}
            />
            <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-[34px]" />
          </div>

          <div className="flex items-center mt-4">
            <input
              type="checkbox"
              id="terms"
              name="termsAccepted"
              checked={formData.termsAccepted}
              onChange={handleChange}
              className="h-4 w-4 text-[#FF4444] focus:ring-[#FF4444] border-gray-300 rounded"
            />
            <label htmlFor="terms" className="ml-2 block text-sm text-gray-600">
              I accept the <Link to="/terms" className="text-[#FF4444] hover:text-[#FF3333]">Terms and Conditions</Link>
            </label>
          </div>
          {errors.terms && (
            <p className="mt-1 text-sm text-red-600">{errors.terms}</p>
          )}

          {/* Turnstile Widget - disabled for now 
          {!isLocalhost && (
            <div className="flex flex-col items-center mb-2">
              <TurnstileWidget onVerify={handleTurnstileVerify} />
              {errors.turnstile && (
                <p className="mt-1 text-sm text-red-600">{errors.turnstile}</p>
              )}
              {!turnstileToken && (
                <p className="mt-1 text-xs text-gray-600">Please complete the security check</p>
              )}
            </div>
          )}
          */}

          <button
            type="submit"
            className="w-full py-2.5 px-4 mt-4 border border-transparent rounded-md text-sm font-medium text-white bg-[#FF4444] hover:bg-[#FF3333] transition-colors disabled:bg-opacity-70 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading ? 'Creating account...' : 'Create account'}
          </button>

          <p className="text-center text-sm text-gray-600 mt-5">
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-medium text-[#FF4444] hover:text-[#FF3333]"
            >
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default Register;